import React from "react";
import { useParams, Link } from "react-router-dom";import { ArrowLeft, Clock, GraduationCap, Award, CheckCircle2, CreditCard, MapPin, Phone, ArrowRight, BookOpen, Users, Briefcase, Download, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { courses, siteInfo, careerOutcomes, courseFAQs } from "../data/mock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { downloadCoursePDF, shareOnWhatsApp } from "../utils/pdfGenerator";
import { useToast } from "../hooks/use-toast";
import { useJsonLd } from "../hooks/useJsonLd";

const CourseDetail = () => {
  const { slug } = useParams();
  const course = courses.find(c => c.slug === slug);
  const { toast } = useToast();

  // Schema.org Course structured data - injected into <head> when this course is viewed
  const courseSchema = course ? {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `${course.title} - ${course.fullTitle}`,
    "description": course.description,
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Associazione Progetto Futuro",
      "sameAs": "https://associazioneprogettofuturo.com",
      "telephone": "+393939457831",
      "email": "progettofuturoapf@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Vibo Valentia",
        "addressRegion": "Calabria",
        "addressCountry": "IT"
      }
    },
    "educationalCredentialAwarded": course.qualification,
    "timeRequired": course.duration,
    "inLanguage": "it-IT",
    "image": course.image,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "EUR",
      "category": course.price,
      "availability": "https://schema.org/InStock",
      "url": `https://associazioneprogettofuturo.com/corsi/${course.slug}`
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "blended",
      "courseWorkload": course.duration,
      "location": {
        "@type": "Place",
        "name": "Vibo Valentia, Calabria, Italia"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127",
      "bestRating": "5"
    }
  } : null;

  useJsonLd(courseSchema, `course-schema-${slug}`);

  // Dynamic page title for SEO
  React.useEffect(() => {
    if (course) {
      const previous = document.title;
      document.title = `${course.title} — ${course.fullTitle} | Associazione Progetto Futuro`;
      return () => { document.title = previous; };
    }
  }, [course]);

  const handleDownloadPDF = () => {
    try {
      downloadCoursePDF(course);
      toast({ title: "PDF scaricato!", description: "Il volantino del corso è stato salvato." });
    } catch (e) {
      toast({ title: "Errore", description: "Impossibile generare il PDF.", variant: "destructive" });
    }
  };

  const handleShareWhatsApp = () => {
    shareOnWhatsApp(course);
  };

  if (!course) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-4xl font-bold text-brand-dark mb-4">Corso non trovato</h1>
        <Link to="/"><Button className="bg-brand hover:bg-brand-dark text-white rounded-full">Torna alla home</Button></Link>
      </div>
    );
  }

  const otherCourses = courses.filter(c => c.slug !== slug).slice(0, 3);
  const outcomes = careerOutcomes[slug] || [];

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-brand text-white overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-30"></div>
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6 pt-12 pb-20 lg:pt-16 lg:pb-28">
          <Link to="/" className="inline-flex items-center gap-2 text-emerald-200 hover:text-white text-sm font-semibold mb-8">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>

          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <Badge className="bg-white/10 hover:bg-white/15 text-emerald-100 border-white/20 mb-5 backdrop-blur-sm">
                {course.tag || "Corso professionale"}
              </Badge>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-4">
                {course.title}
              </h1>
              <p className="text-xl text-emerald-100/90 mb-8">{course.fullTitle}</p>

              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-300" />
                  <div>
                    <div className="text-xs text-emerald-200 uppercase">Durata</div>
                    <div className="font-bold">{course.duration}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-emerald-300" />
                  <div>
                    <div className="text-xs text-emerald-200 uppercase">Teoria</div>
                    <div className="font-bold">{course.theory}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-300" />
                  <div>
                    <div className="text-xs text-emerald-200 uppercase">Pratica</div>
                    <div className="font-bold">{course.practice}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-300" />
                  <div>
                    <div className="text-xs text-emerald-200 uppercase">Pagamento</div>
                    <div className="font-bold">{course.price}</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/richiedi-info">
                  <Button size="lg" className="bg-white text-brand hover:bg-emerald-50 rounded-full h-14 px-8 font-bold">
                    Richiedi informazioni <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href={`tel:${siteInfo.phone}`}>
                  <Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white rounded-full h-14 px-8 font-bold">
                    <Phone className="w-4 h-4 mr-2" /> Informazioni
                  </Button>
                </a>
              </div>

              {/* PDF + WhatsApp share row */}
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={handleDownloadPDF} className="inline-flex items-center gap-2 text-sm text-emerald-100 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full px-5 py-2.5 font-semibold transition-colors">
                  <Download className="w-4 h-4" /> Scarica volantino PDF
                </button>
                <button onClick={handleShareWhatsApp} className="inline-flex items-center gap-2 text-sm text-emerald-100 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full px-5 py-2.5 font-semibold transition-colors">
                  <Share2 className="w-4 h-4" /> Condividi su WhatsApp
                </button>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-white/10 rounded-3xl rotate-3"></div>
                <img src={course.image} alt={course.title} className="relative rounded-3xl w-full aspect-[4/5] object-cover shadow-2xl" />
              </div>
            </div>
          </div>
        </div>

        <svg className="w-full h-16 -mb-1" viewBox="0 0 1440 60" preserveAspectRatio="none">
          <path d="M0,60 C480,10 960,10 1440,60 L1440,60 L0,60 Z" fill="#fafaf7" />
        </svg>
      </section>

      {/* Content */}
      <section className="py-20 bg-[#fafaf7]">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <div>
              <h2 className="font-display text-3xl font-bold text-brand-dark mb-5">Descrizione del corso</h2>
              <p className="text-lg text-brand-dark/80 leading-relaxed">{course.description}</p>
            </div>

            <div>
              <h2 className="font-display text-3xl font-bold text-brand-dark mb-6">Programma formativo</h2>
              <div className="space-y-4">
                {course.modules.map((m, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 border border-emerald-100">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-brand text-white flex items-center justify-center flex-shrink-0 font-display font-bold text-xl">
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="font-bold text-brand-dark text-lg mb-2">{m.title}</h3>
                        <p className="text-brand-dark/70 leading-relaxed">{m.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-brand-light rounded-3xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand text-white flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-display text-2xl font-bold text-brand-dark mb-2">Qualifica finale</h3>
                  <p className="text-brand-dark/80 leading-relaxed">{course.qualification}</p>
                </div>
              </div>
            </div>

            {outcomes.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Briefcase className="w-6 h-6 text-brand" />
                  <h2 className="font-display text-3xl font-bold text-brand-dark">Sbocchi professionali</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {outcomes.map((o, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-emerald-100">
                      <div className="w-9 h-9 rounded-full bg-brand-light text-brand flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <span className="text-brand-dark font-semibold text-sm">{o}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="font-display text-3xl font-bold text-brand-dark mb-6">Domande frequenti</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {courseFAQs.map((f, i) => (
                  <AccordionItem key={i} value={`cf-${i}`} className="bg-white rounded-2xl border border-emerald-100 px-6">
                    <AccordionTrigger className="text-left font-bold text-brand-dark hover:no-underline py-4">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-brand-dark/70 leading-relaxed pb-4">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 space-y-6">
              <div className="bg-white rounded-3xl p-8 border border-emerald-100 shadow-lg">
                <h3 className="font-display text-xl font-bold text-brand-dark mb-5">Perché questo corso?</h3>
                <ul className="space-y-3">
                  {[
                    "Qualifica riconosciuta a livello nazionale",
                    "Docenti esperti del settore",
                    "Tirocini presso strutture reali",
                    "Pagamenti rateizzabili",
                    "Attestato finale con esame"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-brand-dark/80">
                      <CheckCircle2 className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/richiedi-info" className="block mt-6">
                  <Button className="w-full bg-brand hover:bg-brand-dark text-white rounded-full h-12 font-bold">
                    Richiedi informazioni <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button onClick={handleDownloadPDF} className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-brand hover:text-brand-dark bg-brand-light hover:bg-emerald-200 rounded-full px-3 py-2.5 transition-colors">
                    <Download className="w-3.5 h-3.5" /> PDF
                  </button>
                  <button onClick={handleShareWhatsApp} className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-brand hover:text-brand-dark bg-brand-light hover:bg-emerald-200 rounded-full px-3 py-2.5 transition-colors">
                    <Share2 className="w-3.5 h-3.5" /> WhatsApp
                  </button>
                </div>
              </div>

              <div className="bg-brand text-white rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Phone className="w-5 h-5" />
                  <span className="font-semibold">Hai bisogno di aiuto?</span>
                </div>
                <p className="text-sm text-emerald-100/90 mb-4">Contatta i nostri operatori. Siamo qui per te.</p>
                <a href={`tel:${siteInfo.phone}`} className="font-display text-2xl font-bold">{siteInfo.phone}</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-dark mb-10">Altri corsi che potrebbero interessarti</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {otherCourses.map((c) => (
              <Link key={c.id} to={`/corsi/${c.slug}`} className="course-card block">
                <div className="bg-[#fafaf7] rounded-3xl overflow-hidden border border-emerald-100">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={c.image} alt={c.title} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-bold text-brand-dark mb-2">{c.title}</h3>
                    <p className="text-sm text-brand-dark/70 mb-4">{c.shortDescription}</p>
                    <div className="flex items-center gap-1 text-sm font-semibold text-brand">
                      Scopri <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseDetail;
